import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IStorage, LocalStorage } from '@isophi/core-legacy';
import { ChildProfileList, ChildrenService, PaginatedChildProfileListList } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Observable, of, switchMap, tap } from 'rxjs';

import { RouterLinkFactory } from '../../core/router/router-link.factory';
import { HelperService } from '../../core/services/helper.service';
import { EnrollmentHelperService } from '../../shared/services/enrollment-helper.service';
import { LocalStorageConstService } from '../../shared/services/local-storage-const.service';
import { FilterCriteria } from './components/children-filter/filter-criteria.model';

@Component({
  selector: 'app-children',
  templateUrl: './children.component.html',
  styleUrls: ['./children.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChildrenComponent implements OnInit, OnDestroy {
  children$: Observable<PaginatedChildProfileListList>;

  count: number;

  page = 1;

  limit = 10;

  offset = 0;

  markedChildren: ChildProfileList[] = [];

  private filterCriteria: FilterCriteria = {
    group: '',
    withoutClass: false,
    name: '',
    archived: false,
  };

  private childrenReload$ = new BehaviorSubject<void>(null);

  private readonly localStorage: IStorage = new LocalStorage();

  constructor(
    private helperService: HelperService,
    private service: ChildrenService,
    private router: Router,
    private routerLinkFactory: RouterLinkFactory,
    private translocoService: TranslocoService,
    private enrollmentHelperService: EnrollmentHelperService,
    private lSConstService: LocalStorageConstService
  ) {}

  ngOnInit(): void {
    this.helperService.setTitle(location.pathname);
    const savedLimit = this.localStorage.getItem(this.lSConstService.childrenPageLimit);
    if (savedLimit) {
      this.limit = +savedLimit;
    }
    this.children$ = this.childrenReload$.pipe(
      switchMap(() => {
        const loadChildren = (search: string) =>
          this.service.childrenList(
            this.enrollmentHelperService.enrollmentSuccess ? null : this.filterCriteria.group,
            this.filterCriteria.archived,
            this.limit,
            this.offset,
            '',
            search,
            this.filterCriteria.withoutClass ? true : undefined
          );
        const birthNumberSearch = this.toBirthNumberSearch(this.filterCriteria.name);

        // Birth numbers are stored as "xxxxxx/xxxx" - when a search without the slash finds nothing, retry with it
        return loadChildren(this.filterCriteria.name).pipe(
          switchMap((children) => (children.count === 0 && birthNumberSearch ? loadChildren(birthNumberSearch) : of(children))),
          tap(() => (this.enrollmentHelperService.enrollmentSuccess = false))
        );
      }),
      tap((children) => {
        const childText =
          children.count > 1 ? this.translocoService.translate('shared/children') : this.translocoService.translate('shared/child');
        this.helperService.setSmallTextTitle(`${children.count} ${childText}`);
        this.resetMarkedChildren();
      })
    );
  }

  reloadChildren(): void {
    this.childrenReload$.next();
  }

  setFilter(filterCriteria: FilterCriteria): void {
    this.filterCriteria = filterCriteria;
    this.page = 1;
    this.offset = 0;
    this.reloadChildren();
  }

  openChild(id: string): void {
    this.router.navigate(this.routerLinkFactory.childrenDetail(id));
  }

  onPageChange(page: number): void {
    this.offset = (page - 1) * this.limit;
    this.reloadChildren();
  }

  onLimitChange(value: number): void {
    this.limit = value;
    this.page = 1;
    this.offset = 0;
    this.localStorage.setItem(this.lSConstService.childrenPageLimit, String(value));
    this.reloadChildren();
  }

  markAllChildren(children: ChildProfileList[]): void {
    if (this.markedChildren.length === children.length) {
      this.resetMarkedChildren();
    } else {
      this.markedChildren = [...children];
    }
  }

  areAllChildrenMarked(children: ChildProfileList[]): boolean {
    return this.markedChildren.length === children.length && children.length !== 0;
  }

  resetMarkedChildren(): void {
    this.markedChildren = [];
  }

  isChildMarked(child: ChildProfileList): boolean {
    return this.markedChildren.findIndex((ch) => ch.id === child.id) !== -1;
  }

  markChild(child: ChildProfileList): void {
    const childIndex = this.markedChildren.findIndex((ch) => ch.id === child.id);

    const updatedChildren = [...this.markedChildren];
    if (childIndex === -1) {
      updatedChildren.push(child);
    } else {
      updatedChildren.splice(childIndex, 1);
    }

    this.markedChildren = updatedChildren;
  }

  ngOnDestroy(): void {
    this.helperService.clearSmallTextTitle();
  }

  /** Returns the search term as a slashed birth number ("xxxxxx/xxxx") if it is a 9-10 digit number, otherwise null. */
  private toBirthNumberSearch(search: string): string | null {
    const digits = (search ?? '').replace(/\s/g, '');

    return /^\d{9,10}$/.test(digits) ? `${digits.slice(0, 6)}/${digits.slice(6)}` : null;
  }
}
